import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './WorkHub.css';
import WorkHubModal from './WorkHubModal';
import FilesTab from './FilesTab';
import config from '../config';

const AppLogo = ({ app }) => {
  const [imgError, setImgError] = useState(false);

  if (imgError || !app.logoUrl) {
    return <span className="app-icon-fallback">{app.fallbackIcon}</span>;
  }

  return (
    <img
      src={app.logoUrl}
      alt={app.name}
      onError={() => setImgError(true)}
      className="app-logo-img"
    />
  );
};

const AppsIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="appsGradient" x1="4" y1="4" x2="20" y2="20" gradientUnits="userSpaceOnUse">
        <stop stopColor="#6366F1" />
        <stop offset="1" stopColor="#8B5CF6" />
      </linearGradient>
    </defs>
    <rect x="4" y="4" width="6" height="6" rx="1.5" fill="url(#appsGradient)" />
    <rect x="14" y="4" width="6" height="6" rx="1.5" fill="#E5E7FF" />
    <rect x="4" y="14" width="6" height="6" rx="1.5" fill="#E5E7FF" />
    <rect x="14" y="14" width="6" height="6" rx="1.5" fill="url(#appsGradient)" />
  </svg>
);

const FilesIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="fileGradient" x1="6" y1="4" x2="18" y2="20" gradientUnits="userSpaceOnUse">
        <stop stopColor="#0EA5E9" />
        <stop offset="1" stopColor="#6366F1" />
      </linearGradient>
    </defs>
    <path d="M8 4H14L18 8V18C18 19.1046 17.1046 20 16 20H8C6.89543 20 6 19.1046 6 18V6C6 4.89543 6.89543 4 8 4Z" fill="url(#fileGradient)" opacity="0.12" />
    <path d="M8 4H14L18 8V18C18 19.1046 17.1046 20 16 20H8C6.89543 20 6 19.1046 6 18V6C6 4.89543 6.89543 4 8 4Z" stroke="url(#fileGradient)" strokeWidth="1.4" strokeLinejoin="round" />
    <path d="M14 4V8H18" stroke="url(#fileGradient)" strokeWidth="1.4" strokeLinejoin="round" />
  </svg>
);

const WorkHub = ({ isCollapsed = false }) => {
  const [activeTab, setActiveTab] = useState('apps');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [connectedApps, setConnectedApps] = useState([]);

  // Fetch connected apps from Work Hub API
  useEffect(() => {
    const fetchConnectedApps = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) {
          console.log('No auth token found, skipping connected apps fetch');
          return;
        }

        const response = await fetch(`${config.apiUrl}/api/work-hub/connection`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          console.log('Failed to fetch connected apps, using empty list', response.body);
          return;
        }

        const data = await response.json();
        console.log('Connected apps data:', data);
        
        // Transform connected accounts to work apps format (no hardcoding!)
        const apps = (data.connected_accounts || []).map((account, index) => {
          const toolkitMeta = account.toolkit_meta || {};
          const toolkitName = toolkitMeta.name || account.toolkit?.slug || 'Unknown';
          const logoUrl = toolkitMeta.logo || '';
          
          return {
            id: account.id || `connected-${index}`,
            name: toolkitName,
            logoUrl: logoUrl,
            fallbackIcon: '🔧', // Simple fallback
            connected: true,
            account: account,
            description: toolkitMeta.description || '',
            toolsCount: toolkitMeta.tools_count || 0
          };
        });

        setConnectedApps(apps);
        console.log('Transformed connected apps:', apps);
        
      } catch (err) {
        console.error('Error fetching connected apps:', err);
        // Silently fail - just show no connected apps
      }
    };

    fetchConnectedApps();
  }, []);

  // Show empty state if no connected apps
  const workApps = connectedApps.length > 0 ? connectedApps : [];

  // Files are now handled by FilesTab component

  const showIconsOnly = isCollapsed;

  return (
    <div className={`work-hub ${showIconsOnly ? 'compact' : ''}`}>
      <div className="work-hub-header">
        <div className="work-hub-title">Work Hub</div>
        <div className="work-hub-tabs">
          <button
            className={`work-hub-tab ${activeTab === 'apps' ? 'active' : ''}`}
            onClick={() => setActiveTab('apps')}
            type="button"
          >
            {showIconsOnly ? (
              <AppsIcon className="tab-icon-svg" />
            ) : (
              <span className="tab-label">Apps</span>
            )}
          </button>
          <button
            className={`work-hub-tab ${activeTab === 'files' ? 'active' : ''}`}
            onClick={() => setActiveTab('files')}
            type="button"
          >
            {showIconsOnly ? (
              <FilesIcon className="tab-icon-svg" />
            ) : (
              <span className="tab-label">Files</span>
            )}
          </button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'apps' && (
          <motion.div
            key="apps"
            className="work-hub-content"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <div className="work-apps-grid">
              {workApps.map((app) => (
                <motion.button
                  key={app.id}
                  className="work-app-item"
                  whileHover={{ scale: showIconsOnly ? 1 : 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  type="button"
                  title={app.name}
                >
                  <div className="work-app-icon">
                    <AppLogo app={app} />
                  </div>
                  <span className="work-app-name">{app.name}</span>
                </motion.button>
              ))}
              <motion.button
                className="work-app-item add-app"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                type="button"
                title="Add App"
                onClick={() => setIsModalOpen(true)}
              >
                <div className="work-app-icon add-icon">
                  <span className="work-app-letter">+</span>
                </div>
                <span className="work-app-name">Connect</span>
              </motion.button>
            </div>
          </motion.div>
        )}
        {activeTab === 'files' && (
          <motion.div
            key="files"
            className="work-hub-content"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <FilesTab />
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Work Hub Modal */}
      <WorkHubModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </div>
  );
};

export default WorkHub;
